import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Eye, EyeOff, Star } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { adminApi } from '../services/adminApi';
import { Pagination } from '../components/Pagination';
import { COLORS } from '../../theme/colors';

const LIMIT = 30;
// Rating buckets: good / neutral / bad. Status hues, not the categorical series.
const RATING_COLORS = [COLORS.success, COLORS.warning, COLORS.danger];

function RatingStars({ rating }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={14}
          fill={i <= rating ? COLORS.warning : 'none'}
          color={i <= rating ? COLORS.warning : COLORS.borderStrong}
        />
      ))}
    </div>
  );
}

function tabStyle(active) {
  return {
    padding: '8px 4px',
    marginBottom: -1,
    background: 'none',
    border: 'none',
    borderBottom: active ? `2px solid ${COLORS.accent}` : '2px solid transparent',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  };
}

export function FeedbacksPage() {
  const [view, setView] = useState('feedbacks'); // 'feedbacks' | 'comments'

  // Ratings (star / voice feedback)
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [resolvedFilter, setResolvedFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // Inline translation comments (fragment + note)
  const [comments, setComments] = useState([]);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsLoading, setCommentsLoading] = useState(true);

  const loadFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const resolved = resolvedFilter === '' ? undefined : resolvedFilter === 'true';
      const data = await adminApi.getFeedbacks({
        resolved,
        source: sourceFilter || undefined,
        skip: (page - 1) * LIMIT,
        limit: LIMIT
      });
      setFeedbacks(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load feedbacks:', error);
    } finally {
      setLoading(false);
    }
  }, [page, resolvedFilter, sourceFilter]);

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const data = await adminApi.getTranslationComments({
        skip: (commentsPage - 1) * LIMIT,
        limit: LIMIT
      });
      setComments(data.items);
      setCommentsTotal(data.total);
    } catch (error) {
      console.error('Failed to load translation comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, [commentsPage]);

  const loadStats = async () => {
    try {
      const data = await adminApi.getFeedbackStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    loadStats();
  }, []);

  const handleToggleResolved = async (feedback) => {
    try {
      await adminApi.updateFeedback(feedback.id, { resolved: !feedback.resolved });
      loadFeedbacks();
      loadStats();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update feedback');
    }
  };

  const handleDelete = async (feedback) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await adminApi.deleteFeedback(feedback.id);
      loadFeedbacks();
      loadStats();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete feedback');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const commentsTotalPages = Math.ceil(commentsTotal / LIMIT);

  const pieData = stats ? [
    { name: 'Positive', value: stats.positive },
    { name: 'Neutral', value: stats.neutral },
    { name: 'Negative', value: stats.negative }
  ].filter(d => d.value > 0) : [];

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Feedbacks</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}` }}>
        <button type="button" style={tabStyle(view === 'feedbacks')} onClick={() => setView('feedbacks')}>
          Ratings ({stats?.total ?? 0})
        </button>
        <button type="button" style={tabStyle(view === 'comments')} onClick={() => setView('comments')}>
          Comments ({commentsTotal})
        </button>
      </div>

      {view === 'feedbacks' ? (
        <>
          {/* Stats Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 250px', gap: 16, marginBottom: 24 }}>
            <div className="stat-card">
              <div className="stat-card-title">Total</div>
              <div className="stat-card-value">{stats?.total || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-title">Avg Rating</div>
              <div className="stat-card-value">{stats?.avg_rating || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-title">Unresolved</div>
              <div className="stat-card-value" style={{ color: stats?.unresolved > 0 ? 'var(--danger)' : 'var(--success)' }}>
                {stats?.unresolved || 0}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-title">System failures (open)</div>
              <div className="stat-card-value" style={{ color: stats?.system_unresolved > 0 ? 'var(--danger)' : 'var(--success)' }}>
                {stats?.system_unresolved || 0}
              </div>
            </div>
            <div className="stat-card" style={{ padding: 10 }}>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={80}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={20}
                      outerRadius={35}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={RATING_COLORS[index % RATING_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-disabled)', padding: 20 }}>No data</div>
              )}
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <select
              className="admin-input"
              value={resolvedFilter}
              onChange={(e) => { setResolvedFilter(e.target.value); setPage(1); }}
              style={{ width: 180 }}
            >
              <option value="">All Feedbacks</option>
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
            </select>
            <select
              className="admin-input"
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
              style={{ width: 180 }}
            >
              <option value="">All sources</option>
              <option value="translator">Users: translator</option>
              <option value="scan">Users: scan</option>
              <option value="system">System failures</option>
            </select>
          </div>

          <div className="admin-card" style={{ padding: 0 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Rating</th>
                  <th>Comment</th>
                  <th>Categories</th>
                  <th>Status</th>
                  <th>Document ID</th>
                  <th>Created</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>Loading...</td>
                  </tr>
                ) : feedbacks.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No feedbacks found</td>
                  </tr>
                ) : (
                  feedbacks.map(fb => (
                    <tr key={fb.id} style={{ backgroundColor: fb.resolved ? 'transparent' : 'var(--warning-bg)' }}>
                      <td>
                        {fb.source === 'system' ? (
                          <span className="admin-badge admin-badge-danger" title={fb.error_code}>
                            System · {fb.error_code}
                          </span>
                        ) : (
                          <RatingStars rating={fb.rating} />
                        )}
                      </td>
                      <td
                        style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={fb.comment || ''}
                      >
                        {fb.comment || '-'}
                      </td>
                      <td>
                        {fb.categories?.length > 0 ? (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {fb.categories.map((cat, i) => (
                              <span key={i} className="admin-badge admin-badge-info" style={{ fontSize: 10 }}>
                                {cat}
                              </span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td>
                        <span className={`admin-badge ${fb.resolved ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                          {fb.resolved ? 'Resolved' : 'Pending'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <a
                          href={`/admin/documents?search=${fb.document_id}`}
                          style={{ color: 'var(--accent)', textDecoration: 'none' }}
                          title="View document"
                        >
                          {fb.document_id}
                        </a>
                      </td>
                      <td>{new Date(fb.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleToggleResolved(fb)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                            title={fb.resolved ? 'Mark as unresolved' : 'Mark as resolved'}
                          >
                            {fb.resolved ? (
                              <EyeOff size={16} color={COLORS.textMuted} />
                            ) : (
                              <Eye size={16} color={COLORS.success} />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(fb)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                            title="Delete"
                          >
                            <Trash2 size={16} color={COLORS.danger} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      ) : (
        <>
          <div className="admin-card" style={{ padding: 0 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Fragment</th>
                  <th style={{ width: '35%' }}>Comment</th>
                  <th>Document</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {commentsLoading ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 40 }}>Loading...</td>
                  </tr>
                ) : comments.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No comments yet</td>
                  </tr>
                ) : (
                  comments.map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontStyle: 'italic', color: 'var(--warning-text)' }}>“{c.quote}”</td>
                      <td style={{ whiteSpace: 'pre-wrap' }}>{c.comment}</td>
                      <td style={{ fontSize: 12 }}>
                        <a
                          href={`/admin/documents?search=${c.document_id}`}
                          style={{ color: 'var(--accent)', textDecoration: 'none' }}
                          title={c.document_id}
                        >
                          {c.document_name}
                        </a>
                      </td>
                      <td>{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination currentPage={commentsPage} totalPages={commentsTotalPages} onPageChange={setCommentsPage} />
        </>
      )}
    </div>
  );
}
